import { getProjectDrawings } from "@/features/drawings/DrawingsSection";
import { AddDrawingModal } from "@/features/drawings/modals/addDrawings/AddDrawingModal";
import { useModals } from "@/features/modals/hooks";
import styled from "@emotion/styled";
import { NextBestAction, type Opportunity } from "@repo/api";
import {
  LightMode,
  ListView,
  ListViewItem,
  Text,
  Variables,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { useCustomer } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useState } from "react";

import {
  RECOMMENDED_ACTION_IDS,
  isProjectInactive,
  nextBestActionConfig,
} from "./utils";

const Heading = styled(Text)`
  margin-bottom: ${Variables.space50};
`;

const Container = styled.div`
  padding-bottom: ${Variables.space100};
`;

const RecommendedActionList = styled(ListView)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Variables.space100};

  > * {
    flex: 0 1 calc(33% - ${Variables.space100});
    width: auto;
  }
`;

const RecommendedActionListItem = styled(ListViewItem)`
  border: 1px solid ${LightMode.colourNeutral3};
`;

interface RecommendedActionsProps {
  actions?: NextBestAction[];
  opportunity: Opportunity;
}

export const RecommendedActions = ({
  actions,
  opportunity,
}: RecommendedActionsProps) => {
  const [isDrawingModalOpen, setIsDrawingModalOpen] = useState(false);

  const projectDrawings = getProjectDrawings(opportunity);
  const projectDrawingIds = projectDrawings?.ids ?? [];

  const { customer } = useCustomer(opportunity?.id);
  const { pushModal } = useModals();

  const handleTaskActionClick = (action: NextBestAction) => {
    trackAnalytics(
      `google_ai.recommended_action_click_${action.toLowerCase()}`
    );

    switch (action) {
      case NextBestAction.ASSIGN_PROJECT:
        trackAnalytics("google_ai.open_project_details_modal");
        pushModal({ type: "PROJECT_DETAILS" });
        break;

      case NextBestAction.SCHEDULE_APPOINTMENT:
      case NextBestAction.BOOK_FOLLOW_UP_APPOINTMENT:
        trackAnalytics("google_ai.open_book_appointment_modal");
        pushModal({ type: "BOOK_APPOINTMENT" });
        break;

      case NextBestAction.PRE_CALL:
      case NextBestAction.FOLLOW_UP:
        trackAnalytics("google_ai.open_task_modal");
        pushModal({ type: "TASK", context: "PROJECT" });
        break;

      case NextBestAction.NEED_ANALYSIS_QUESTIONNAIRE:
        trackAnalytics("google_ai.open_questionnaire_modal");
        pushModal({
          type: "QUESTIONNAIRE",
          opportunityId: opportunity.id,
          selectedHfbs: opportunity?.hfb || [],
        });
        break;

      case NextBestAction.ATTACH_PLANNER:
        trackAnalytics("google_ai.open_add_drawing_modal");
        setIsDrawingModalOpen(true);
        break;

      case NextBestAction.UPLOAD_FILES: {
        trackAnalytics("google_ai.scroll_to_files_section");
        const element = document.getElementById(
          RECOMMENDED_ACTION_IDS.FILES_SECTION
        );
        element?.scrollIntoView({ behavior: "smooth" });
        break;
      }

      case NextBestAction.ATTACH_QUOTE_OR_ORDER:
      case NextBestAction.ATTACH_INSTALLATION_OR_SERVICE: {
        trackAnalytics("google_ai.scroll_to_orders_section");
        const element = document.getElementById(
          RECOMMENDED_ACTION_IDS.ORDERS_SECTION
        );
        element?.scrollIntoView({ behavior: "smooth" });
        break;
      }

      case NextBestAction.UPDATE_BUDGET:
      case NextBestAction.UPDATE_EXPECTED_PURCHASE_DATE:
        trackAnalytics("google_ai.open_project_details_modal");
        pushModal({ type: "NEED_ANALYSIS" });
        break;

      default:
        return null;
    }
  };

  return (
    <Container>
      <Heading textStyle="Heading.XS">{t("label.recommended_actions")}</Heading>
      {isProjectInactive(opportunity) ? (
        <Text>{t("prompt.no_recommended_actions_for_closed_project")}</Text>
      ) : actions?.length ? (
        <RecommendedActionList id="recommented-actions-list">
          {actions.map((action) => {
            const { title, icon } = nextBestActionConfig[action];
            return (
              <RecommendedActionListItem
                key={action}
                title={t(title)}
                leadingIcon={icon}
                control="navigational"
                onClick={() => handleTaskActionClick(action)}
                size="small"
                inset
              />
            );
          })}
        </RecommendedActionList>
      ) : (
        <Text>{t("prompt.no_recommended_actions")}</Text>
      )}

      {Boolean(opportunity?.id) && (
        <AddDrawingModal
          isOpen={isDrawingModalOpen}
          closeModal={() => {
            trackAnalytics("google_ai.close_add_drawing_modal");
            setIsDrawingModalOpen(false);
          }}
          opportunityId={opportunity.id.toString()}
          existingDrawingIds={projectDrawingIds}
          customer={customer}
        />
      )}
    </Container>
  );
};
